import { FC } from 'react';

import { useOptions } from '@core/contexts/OptionsContext';
import { t } from '@core/i18n';
import { WithStyles, withStyles } from '@core/theme/utils/with-styles';
import { Flex } from '@shared/components/flex';
import { SingleSelect } from '@shared/components/select-new/single-select';
import { TextField } from '@shared/components/text-field';
import { ButtonSize } from '@shared/types/common/button';
import { InputSize } from '@shared/types/common/input';

import { styles } from './CompanyAddressForm.styles';

export interface CompanyAddressFormData {
  addressLine1: string;
  addressLine2: string;
  county: string;
  country: string;
  postcode: string;
  town: string;
}

export interface CompanyAddressFormProps extends WithStyles<typeof styles> {
  disabled: boolean;
  formData: CompanyAddressFormData;
  setFormData: (data: CompanyAddressFormData) => void;
}

const CompanyAddressFormComponent: FC<CompanyAddressFormProps> = ({ classes, disabled, formData, setFormData }) => {
  const { countryOptions } = useOptions();

  const handleFormChange: ChangeCallback<CompanyAddressFormData> = (key) => (value) => {
    setFormData({
      ...formData,
      [key]: value,
    });
  };

  return (
    <div className={classes.root}>
      <Flex alignItems="center" wrap="nowrap" classes={{ root: classes.row }}>
        <div className={classes.rowElement}>
          <span className={classes.label}>{t('address_line_1')}</span>
          <TextField
            disabled={disabled}
            fullWidth
            size={InputSize.medium}
            value={formData.addressLine1}
            onChange={(e) => handleFormChange('addressLine1')(e.target.value)}
            placeholder="1-13 St Giles High Street"
          />
        </div>
        <div className={classes.rowElement}>
          <span className={classes.label}>{t('address_line_2')}</span>
          <TextField
            disabled={disabled}
            fullWidth
            size={InputSize.medium}
            value={formData.addressLine2}
            onChange={(e) => handleFormChange('addressLine2')(e.target.value)}
            placeholder="Covent Garden"
          />
        </div>
      </Flex>
      <Flex alignItems="center" wrap="nowrap" classes={{ root: classes.row }}>
        <div className={classes.rowElement}>
          <span className={classes.label}>{t('town')}</span>
          <TextField
            disabled={disabled}
            fullWidth
            size={InputSize.medium}
            value={formData.town}
            onChange={(e) => handleFormChange('town')(e.target.value)}
            placeholder="London"
          />
        </div>
        <div className={classes.rowElement}>
          <span className={classes.label}>{t('county')}</span>
          <TextField
            disabled={disabled}
            fullWidth
            size={InputSize.medium}
            value={formData.county}
            onChange={(e) => handleFormChange('county')(e.target.value)}
            placeholder="Greater London"
          />
        </div>
      </Flex>
      <Flex alignItems="center" wrap="nowrap" classes={{ root: classes.row }}>
        <div className={classes.rowElement}>
          <span className={classes.label}>{t('country')}</span>
          <SingleSelect
            disabled={disabled}
            size={ButtonSize.medium}
            value={formData.country}
            onChange={(value) => handleFormChange('country')(value as string)}
            options={countryOptions}
            placeholder={t('please_select')}
            classes={{ root: classes.countrySelect }}
          />
        </div>
        <div className={classes.rowElement}>
          <span className={classes.label}>{t('postcode')}</span>
          <TextField
            disabled={disabled}
            fullWidth
            size={InputSize.medium}
            value={formData.postcode}
            onChange={(e) => handleFormChange('postcode')(e.target.value)}
            placeholder="WC2H 8AG"
          />
        </div>
      </Flex>
    </div>
  );
};

export const CompanyAddressForm = withStyles(styles)(CompanyAddressFormComponent);
